import { useEffect } from 'react';
import Router from 'next/router';

/**
 * @summary Save the current scroll position to session storage.
 * @param {string} url Current URL.
 */
export function saveScrollPos(url) {
	const scrollPos = { x: window.scrollX, y: window.scrollY };
	sessionStorage.setItem(url, JSON.stringify(scrollPos));
}

/**
 * @summary Restore the current scroll position for this URL from session storage.
 * @param {string} url Current URL.
 */
export function restoreScrollPos(url) {
	const scrollPos = JSON.parse(sessionStorage.getItem(url));
	if (scrollPos) {
		window.scrollTo(scrollPos.x, scrollPos.y);
	}
}

/**
 * @summary Reset the scroll position for the current path
 * by removing the item for it from session storage.
 * @param {string} pathname 
 */
export function resetScrollPaths(pathname) {
	Object.keys(sessionStorage).forEach((key) => {
		const keyPath = key.split('?')[0];
		if (keyPath === pathname) {
			sessionStorage.removeItem(key);
		}
	});
}

/**
 * @summary Use scroll restoration.
 * @param {import('next/router').Router} router 
 */
export function useScrollRestoration(router) {
	useEffect(() => {
		if ('scrollRestoration' in window.history) {
			let shouldScrollRestore = false;
			window.history.scrollRestoration = 'manual';
			restoreScrollPos(router.asPath);

			const onBeforeUnload = (event) => {
				saveScrollPos(router.asPath);
				// eslint-disable-next-line no-param-reassign
				delete event.returnValue;
			};

			const onRouteChangeStart = () => {
				saveScrollPos(router.asPath);
			};

			const onRouteChangeComplete = (url) => {
				if (shouldScrollRestore) {
					shouldScrollRestore = false;
					restoreScrollPos(url);
				}
			};

			window.addEventListener('beforeunload', onBeforeUnload);
			Router.events.on('routeChangeStart', onRouteChangeStart);
			Router.events.on('routeChangeComplete', onRouteChangeComplete);
			Router.beforePopState(() => {
				shouldScrollRestore = true;
				return true;
			});

			return () => {
				window.removeEventListener('beforeunload', onBeforeUnload);
				Router.events.off('routeChangeStart', onRouteChangeStart);
				Router.events.off('routeChangeComplete', onRouteChangeComplete);
				Router.beforePopState(() => true);
			};
		}

		return null;
	}, [router]);
}
